/**
 * Player Comparison Component
 * Displays visual comparison charts for selected members
 */

const { useState, useEffect, useRef } = React;

export function PlayerComparison({ members = [], memberContributions = [], onClose, initialMembers = [] }) {
  const [selectedMembers, setSelectedMembers] = useState(initialMembers);
  const [comparisonType, setComparisonType] = useState('total'); // total, daily, medals, trend
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const chartRefs = useRef({});

  // Initialize selected members if provided
  useEffect(() => {
    if (initialMembers && initialMembers.length > 0) {
      setSelectedMembers(initialMembers);
    }
  }, [initialMembers]);

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get total score for a member
  const getMemberTotal = (memberId) => {
    return memberContributions
      .filter(c => c.member_id === memberId)
      .reduce((sum, c) => sum + (parseFloat(c.score) || 0), 0);
  };

  // Get daily scores for a member
  const getMemberDailyScores = (memberId) => {
    const daily = [0, 0, 0, 0, 0, 0];
    memberContributions
      .filter(c => c.member_id === memberId)
      .forEach(c => {
        if (c.day_number >= 1 && c.day_number <= 6) {
          daily[c.day_number - 1] += parseFloat(c.score) || 0;
        }
      });
    return daily;
  };

  // Get medals for a member
  const getMemberMedals = (memberId) => {
    const memberName = members.find(m => m.id === memberId)?.display_name || 'Unknown';
    const medals = { gold: 0, silver: 0, bronze: 0 };
    
    // Group by event and day
    const eventDays = {};
    memberContributions
      .filter(c => c.member_id === memberId)
      .forEach(c => {
        const key = `${c.event_id}_${c.day_number}`;
        if (!eventDays[key]) eventDays[key] = [];
        eventDays[key].push(c);
      });

    // For each day in each event, rank against all members
    Object.values(eventDays).forEach(dayContribs => {
      if (dayContribs.length === 0) return;
      const eventId = dayContribs[0].event_id;
      const dayNum = dayContribs[0].day_number;
      
      // Get all members' scores for this day
      const dayScores = [];
      memberContributions
        .filter(c => c.event_id === eventId && c.day_number === dayNum)
        .forEach(c => {
          const mdata = members.find(m => m.id === c.member_id);
          dayScores.push({
            name: mdata?.display_name || 'Unknown',
            score: parseFloat(c.score) || 0,
            memberId: c.member_id
          });
        });

      dayScores.sort((a, b) => b.score - a.score);
      
      const memberRank = dayScores.findIndex(s => s.memberId === memberId);
      if (memberRank === 0) medals.gold++;
      else if (memberRank === 1) medals.silver++;
      else if (memberRank === 2) medals.bronze++;
    });

    return medals;
  };

  // Get average score per contribution
  const getMemberAverage = (memberId) => {
    const contribs = memberContributions.filter(c => c.member_id === memberId);
    if (contribs.length === 0) return 0;
    const total = contribs.reduce((sum, c) => sum + (parseFloat(c.score) || 0), 0);
    return total / contribs.length;
  };

  // Draw comparison chart
  useEffect(() => {
    if (selectedMembers.length === 0 || !window.Chart) return;

    const chartCanvas = chartRefs.current.comparisonChart;
    if (!chartCanvas) return;

    const ctx = chartCanvas.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart if it exists
    if (chartCanvas.chart) {
      chartCanvas.chart.destroy();
    }

    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
      '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'
    ];

    let chartConfig = {};

    if (comparisonType === 'total') {
      const data = selectedMembers.map(memberId => {
        const member = members.find(m => m.id === memberId);
        return getMemberTotal(memberId);
      });

      chartConfig = {
        type: 'bar',
        data: {
          labels: selectedMembers.map(mid => members.find(m => m.id === mid)?.display_name || 'Unknown'),
          datasets: [{
            label: 'Total Score',
            data: data,
            backgroundColor: colors.slice(0, selectedMembers.length),
            borderRadius: 6,
            borderWidth: 0
          }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: { display: false },
            title: { display: true, text: 'Total Scores Comparison', color: 'rgba(255,255,255,0.8)' }
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            },
            y: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            }
          }
        }
      };
    } else if (comparisonType === 'daily') {
      const datasets = selectedMembers.map((memberId, idx) => {
        const member = members.find(m => m.id === memberId);
        return {
          label: member?.display_name || 'Unknown',
          data: getMemberDailyScores(memberId),
          borderColor: colors[idx % colors.length],
          backgroundColor: colors[idx % colors.length] + '33',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        };
      });

      chartConfig = {
        type: 'line',
        data: {
          labels: ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6'],
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: { labels: { color: 'rgba(255,255,255,0.8)' }, display: true },
            title: { display: true, text: 'Daily Performance Comparison', color: 'rgba(255,255,255,0.8)' }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            },
            x: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            }
          }
        }
      };
    } else if (comparisonType === 'medals') {
      const goldData = selectedMembers.map(mid => getMemberMedals(mid).gold);
      const silverData = selectedMembers.map(mid => getMemberMedals(mid).silver);
      const bronzeData = selectedMembers.map(mid => getMemberMedals(mid).bronze);

      chartConfig = {
        type: 'bar',
        data: {
          labels: selectedMembers.map(mid => members.find(m => m.id === mid)?.display_name || 'Unknown'),
          datasets: [
            { label: '🥇 Gold', data: goldData, backgroundColor: '#FFD700', borderRadius: 4 },
            { label: '🥈 Silver', data: silverData, backgroundColor: '#C0C0C0', borderRadius: 4 },
            { label: '🥉 Bronze', data: bronzeData, backgroundColor: '#CD7F32', borderRadius: 4 }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: { labels: { color: 'rgba(255,255,255,0.8)' } },
            title: { display: true, text: 'Medals Comparison', color: 'rgba(255,255,255,0.8)' }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            },
            x: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            }
          }
        }
      };
    } else if (comparisonType === 'trend') {
      const datasets = selectedMembers.map((memberId, idx) => {
        const member = members.find(m => m.id === memberId);
        const contributionsByEvent = {};
        
        memberContributions
          .filter(c => c.member_id === memberId)
          .forEach(c => {
            if (!contributionsByEvent[c.event_id]) {
              contributionsByEvent[c.event_id] = 0;
            }
            contributionsByEvent[c.event_id] += parseFloat(c.score) || 0;
          });

        return {
          label: member?.display_name || 'Unknown',
          data: Object.values(contributionsByEvent).slice(0, 10),
          borderColor: colors[idx % colors.length],
          backgroundColor: colors[idx % colors.length] + '33',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        };
      });

      chartConfig = {
        type: 'line',
        data: {
          labels: Array.from({ length: 10 }, (_, i) => `Event ${i + 1}`),
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: { labels: { color: 'rgba(255,255,255,0.8)' }, display: true },
            title: { display: true, text: 'Score Trend (Last 10 Events)', color: 'rgba(255,255,255,0.8)' }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(255,255,255,0.1)' },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            },
            x: {
              grid: { display: false },
              ticks: { color: 'rgba(255,255,255,0.7)' }
            }
          }
        }
      };
    }

    chartCanvas.chart = new Chart(ctx, chartConfig);
  }, [selectedMembers, comparisonType, members, memberContributions]);

  return (
    <div style={{ padding: '16px', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '8px', maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700' }}>📊 Player Comparison</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
      </div>

      {selectedMembers.length > 0 && (
        <>
          {/* Comparison Type Selector */}
          <div style={{ display: 'flex', gap: '5px', marginBottom: '16px', flexWrap: 'nowrap' }}>
            {[
              { value: 'total', label: '📊 Total' },
              { value: 'daily', label: '📈 Daily' },
              { value: 'medals', label: '🏆 Medals' },
              { value: 'trend', label: '📉 Trend' }
            ].map(type => (
              <button
                key={type.value}
                onClick={() => setComparisonType(type.value)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: comparisonType === type.value ? 'var(--accent-primary)' : 'rgba(255,255,255,0.1)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {type.label}
              </button>
            ))}
          </div>

          {/* Stats Summary - VS Mirror Comparison */}
          {selectedMembers.length === 2 ? (
            <div style={{ 
              width: isMobile ? '100%' : 'auto',
              padding: isMobile ? '12px' : '16px', 
              backgroundColor: 'rgba(255,107,75,0.05)', 
              borderRadius: '10px', 
              border: '1px solid rgba(255,107,75,0.15)',
              boxSizing: 'border-box'
            }}>
              {(() => {
                const memberA = members.find(m => m.id === selectedMembers[0]);
                const memberB = members.find(m => m.id === selectedMembers[1]);
                const totalA = getMemberTotal(selectedMembers[0]);
                const totalB = getMemberTotal(selectedMembers[1]);
                const avgA = getMemberAverage(selectedMembers[0]);
                const avgB = getMemberAverage(selectedMembers[1]);
                const medalsA = getMemberMedals(selectedMembers[0]);
                const medalsB = getMemberMedals(selectedMembers[1]);
                
                const formatScore = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                const formatScoreShort = (num) => (num / 1000000).toFixed(2) + 'M';
                
                return (
                  <div style={{ display: 'grid', gap: isMobile ? '2px' : '4px' }}>
                    {/* Header with player names */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '4px' : '8px', width: '100%' }}>
                      <div style={{ flex: 1, textAlign: 'right', fontWeight: '700', color: 'var(--accent-primary)', fontSize: isMobile ? '12px' : '13px', paddingRight: isMobile ? '8px' : '16px' }}>
                        {memberA?.display_name}
                      </div>
                      <div style={{ fontSize: isMobile ? '10px' : '12px', color: 'var(--text-muted)', fontWeight: '600', textAlign: 'center', minWidth: isMobile ? '24px' : '40px' }}>VS</div>
                      <div style={{ flex: 1, textAlign: 'left', fontWeight: '700', color: 'var(--accent-secondary)', fontSize: isMobile ? '12px' : '13px', paddingLeft: isMobile ? '8px' : '16px' }}>
                        {memberB?.display_name}
                      </div>
                    </div>
                    
                    {/* Total Score */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: isMobile ? '10px' : '12px', width: '100%' }}>
                      <div style={{ flex: 1, textAlign: 'right', paddingRight: isMobile ? '8px' : '16px' }}>
                        <strong style={{ color: 'var(--accent-primary)', fontFamily: 'monospace' }}>{formatScoreShort(totalA)}</strong>
                      </div>
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: isMobile ? '9px' : '11px', minWidth: isMobile ? '24px' : '40px' }}>TOTAL</div>
                      <div style={{ flex: 1, textAlign: 'left', paddingLeft: isMobile ? '8px' : '16px' }}>
                        <strong style={{ color: 'var(--accent-secondary)', fontFamily: 'monospace' }}>{formatScoreShort(totalB)}</strong>
                      </div>
                    </div>
                    
                    {/* Average */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: isMobile ? '10px' : '12px', width: '100%' }}>
                      <div style={{ flex: 1, textAlign: 'right', paddingRight: isMobile ? '8px' : '16px' }}>
                        <strong style={{ color: 'var(--accent-primary)', fontFamily: 'monospace' }}>{avgA.toFixed(1)}</strong>
                      </div>
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: isMobile ? '9px' : '11px', minWidth: isMobile ? '24px' : '40px' }}>AVG</div>
                      <div style={{ flex: 1, textAlign: 'left', paddingLeft: isMobile ? '8px' : '16px' }}>
                        <strong style={{ color: 'var(--accent-secondary)', fontFamily: 'monospace' }}>{avgB.toFixed(1)}</strong>
                      </div>
                    </div>
                    
                    {/* Medals */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: isMobile ? '10px' : '12px', width: '100%' }}>
                      <div style={{ flex: 1, textAlign: 'right', paddingRight: isMobile ? '8px' : '16px' }}>
                        <span style={{ color: 'var(--accent-primary)' }}>🥇{medalsA.gold} 🥈{medalsA.silver} 🥉{medalsA.bronze}</span>
                      </div>
                      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: isMobile ? '9px' : '11px', minWidth: isMobile ? '24px' : '40px' }}>MEDALS</div>
                      <div style={{ flex: 1, textAlign: 'left', paddingLeft: isMobile ? '8px' : '16px' }}>
                        <span style={{ color: 'var(--accent-secondary)' }}>🥇{medalsB.gold} 🥈{medalsB.silver} 🥉{medalsB.bronze}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: '10px', marginBottom: '16px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              {selectedMembers.map((memberId, idx) => {
                const member = members.find(m => m.id === memberId);
                const total = getMemberTotal(memberId);
                const avg = getMemberAverage(memberId);
                const medals = getMemberMedals(memberId);
                
                const formatScore = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                
                return (
                  <div key={memberId} style={{ 
                    padding: '8px 12px', 
                    backgroundColor: 'rgba(255,107,75,0.04)', 
                    borderRadius: '6px', 
                    border: '1px solid rgba(255,107,75,0.1)',
                    fontSize: isMobile ? '11px' : '12px'
                  }}>
                    <div style={{ fontWeight: '700', color: 'var(--accent-primary)', marginBottom: '4px', fontSize: isMobile ? '12px' : '13px' }}>
                      {member?.display_name || 'Unknown'}
                    </div>
                    <div style={{ lineHeight: '1.5', color: 'var(--text-secondary)', fontSize: isMobile ? '10px' : '11px' }}>
                      <div>Total: <strong style={{ color: 'var(--accent-primary)', fontFamily: 'monospace' }}>{formatScore(total)}</strong></div>
                      <div>Avg: <strong style={{ color: 'var(--accent-secondary)', fontFamily: 'monospace' }}>{avg.toFixed(1)}</strong></div>
                      <div>🥇{medals.gold} 🥈{medals.silver} 🥉{medals.bronze}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Chart */}
          <div style={{ padding: '4px', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '5px' }}>
            <canvas
              ref={el => chartRefs.current.comparisonChart = el}
              style={{ maxHeight: '400px' }}
            />
          </div>
        </>
      )}
    </div>
  );
}
